"use client"

import { clsx } from "clsx"
import type { ComponentType, InputHTMLAttributes, ReactNode, Ref } from "react"
import { useId } from "react"

type WithFloatingLabelProps = InputHTMLAttributes<HTMLInputElement> & {
  readonly ref?: Ref<HTMLInputElement>
  readonly children?: ReactNode
}

type InputComponent = ComponentType<
  InputHTMLAttributes<HTMLInputElement> & {
    readonly ref?: Ref<HTMLInputElement>
  }
>

export function withFloatingLabel(Component: InputComponent) {
  return function ComponentWithFloatingLabel({
    children,
    className,
    placeholder,
    ref,
    ...rest
  }: WithFloatingLabelProps) {
    const inputId = useId()

    return (
      <div className="h-full">
        <Component
          className={clsx(
            className,
            "peer h-full placeholder:text-transparent",
          )}
          id={inputId}
          placeholder={placeholder ?? ""}
          ref={ref}
          // biome-ignore lint: allow passthrough props here
          {...rest}
        />
        <label
          className={clsx(
            "pointer-events-none absolute top-1 left-0 origin-top-left -translate-y-full scale-75 select-none whitespace-nowrap py-0.5 text-inherit text-lg transition-transform ease-linear motion-reduce:transition-none lg:text-base",
            "peer-focus:not(:read-only):!text-error-500 peer-focus:not(:read-only):left-0 peer-focus:not(:read-only):max-w-none peer-focus:not(:read-only):-translate-y-full peer-focus:not(:read-only):scale-75 peer-focus:not(:read-only):peer-placeholder-shown:top-1 peer-focus:not(:read-only):peer-placeholder-shown:scale-75 peer-focus:not(:read-only):peer-placeholder-shown:py-0.25 peer-focus:not(:read-only):peer-placeholder-shown:text-dim",
            "peer-placeholder-shown:top-1/2 peer-placeholder-shown:left-1 peer-placeholder-shown:max-w-full peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:truncate peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-dim",
            "peer-required:after:inline-block peer-required:after:align-text-top peer-required:after:text-error-400 peer-required:after:text-sm peer-required:after:leading-none peer-required:after:content-['*']",
          )}
          htmlFor={inputId}
        >
          {placeholder}
        </label>

        {children}
      </div>
    )
  }
}
